import { useMemo, useState } from 'react';
import { BookOpen, GitBranch, Lightbulb, ListOrdered, Table2 } from 'lucide-react';
import { cn } from '@/lib/utils';

function TreeLines({ depth, isLast, ancestorLast }) {
  if (depth === 0) return null;
  return (
    <span className="inline-flex shrink-0 select-none" aria-hidden>
      {ancestorLast.slice(1).map((last, i) => (
        <span key={i} className="w-5 inline-block text-slate-600">
          {last ? ' ' : '│'}
        </span>
      ))}
      <span className="w-5 inline-block text-slate-500">{isLast ? '└── ' : '├── '}</span>
    </span>
  );
}

function ConceptNode({ node, depth, isLast, ancestorLast }) {
  const children = node.children || [];
  const [open, setOpen] = useState(depth < 2);

  return (
    <div>
      <div className="flex items-start gap-1 min-w-0">
        <TreeLines depth={depth} isLast={isLast} ancestorLast={ancestorLast} />
        <button
          type="button"
          className="text-left min-w-0 flex-1 py-0.5 group"
          onClick={() => children.length > 0 && setOpen((v) => !v)}
        >
          <span className="text-sm font-medium text-violet-100 flex items-center gap-1.5">
            {children.length > 0 ? (
              <span className="text-[10px] text-slate-500">{open ? '▼' : '▶'}</span>
            ) : null}
            {node.name}
            {node.level != null ? (
              <span className="text-[10px] text-slate-600 font-normal">L{node.level}</span>
            ) : null}
          </span>
          {node.description ? (
            <p className="text-xs text-slate-400 mt-0.5">{node.description}</p>
          ) : null}
          {node.why_it_matters ? (
            <p className="text-[11px] text-amber-400/80 mt-0.5">
              Why: {node.why_it_matters}
            </p>
          ) : null}
          {node.prerequisites?.length > 0 ? (
            <p className="text-[10px] text-slate-500 mt-0.5">
              Prerequisites: {node.prerequisites.join(', ')}
            </p>
          ) : null}
        </button>
      </div>
      {open && children.map((child, idx) => (
        <ConceptNode
          key={`${child.name}-${idx}`}
          node={child}
          depth={depth + 1}
          isLast={idx === children.length - 1}
          ancestorLast={[...ancestorLast, isLast]}
        />
      ))}
    </div>
  );
}

const TABS = [
  { id: 'tree', label: 'Concept tree', icon: GitBranch },
  { id: 'path', label: 'Learning path', icon: ListOrdered },
  { id: 'deps', label: 'Dependencies', icon: Table2 },
  { id: 'extras', label: 'Insights', icon: Lightbulb },
];

export function ConceptTreeV3Panel({ data, className }) {
  const [tab, setTab] = useState('tree');

  const root = data?.concept_tree;
  const ascii = data?.ascii_tree;

  const depRows = useMemo(
    () => data?.concept_relationships_table?.length
      ? data.concept_relationships_table
      : data?.knowledge_dependency_graph || [],
    [data],
  );

  if (!data) {
    return (
      <p className="text-sm text-slate-500">Generate a concept tree to see results.</p>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex flex-wrap gap-1 border-b border-slate-700 pb-2">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-sm',
              tab === id
                ? 'bg-violet-500/20 text-violet-200 border border-violet-500/30'
                : 'text-slate-400 hover:text-slate-200',
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'tree' && (
        <div className="space-y-4">
          {ascii ? (
            <pre className="text-xs font-mono text-emerald-300/90 bg-slate-950/60 border border-slate-700 rounded-lg p-3 overflow-x-auto whitespace-pre">
              {ascii}
            </pre>
          ) : null}
          {root ? (
            <div className="font-mono text-[13px] leading-relaxed border border-slate-700/60 rounded-lg p-3 bg-slate-900/40">
              <div className="flex items-center gap-2 mb-2 text-violet-200">
                <BookOpen className="w-4 h-4" />
                <span className="font-semibold">{data.topic || root.name}</span>
              </div>
              {(root.children || []).map((child, idx) => (
                <ConceptNode
                  key={`${child.name}-${idx}`}
                  node={child}
                  depth={1}
                  isLast={idx === (root.children?.length || 0) - 1}
                  ancestorLast={[true]}
                />
              ))}
              {!root.children?.length ? (
                <ConceptNode node={root} depth={0} isLast ancestorLast={[true]} />
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {tab === 'path' && (
        <div className="space-y-4 text-sm">
          <section>
            <h4 className="text-xs font-semibold text-violet-300 mb-2">Learning path</h4>
            <ol className="list-decimal pl-5 space-y-1 text-slate-300">
              {(data.learning_path || []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </section>
          {data.roadmap ? (
            <section className="grid sm:grid-cols-3 gap-3">
              {['beginner', 'intermediate', 'advanced'].map((level) => (
                <div key={level} className="rounded border border-slate-700 bg-slate-900/50 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{level}</p>
                  <ul className="text-xs text-slate-300 space-y-0.5">
                    {(data.roadmap[level] || []).map((x) => (
                      <li key={x}>• {x}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ) : null}
          {data.top_concepts_ranked?.length ? (
            <section>
              <h4 className="text-xs font-semibold text-violet-300 mb-2">Top concepts</h4>
              <ol className="space-y-1 text-xs text-slate-300">
                {data.top_concepts_ranked.map((row) => (
                  <li key={row.rank}>
                    <span className="text-violet-400">#{row.rank}</span> {row.name}
                    {row.importance_reason ? (
                      <span className="text-slate-500"> — {row.importance_reason}</span>
                    ) : null}
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>
      )}

      {tab === 'deps' && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-700">
                <th className="py-2 pr-3">Concept</th>
                <th className="py-2 pr-3">Depends on</th>
                <th className="py-2">Enables</th>
              </tr>
            </thead>
            <tbody>
              {depRows.map((row) => (
                <tr key={row.concept} className="border-b border-slate-800 text-slate-300">
                  <td className="py-2 pr-3 font-medium text-violet-200">{row.concept}</td>
                  <td className="py-2 pr-3">{(row.depends_on || []).join(', ') || '—'}</td>
                  <td className="py-2">{(row.enables || []).join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'extras' && (
        <div className="space-y-4 text-sm text-slate-300">
          {data.feynman_explanation ? (
            <section>
              <h4 className="text-xs font-semibold text-amber-300 mb-2">Feynman explanation</h4>
              <p className="text-sm leading-relaxed">{data.feynman_explanation}</p>
            </section>
          ) : null}
          {data.real_world_applications?.length ? (
            <section>
              <h4 className="text-xs font-semibold text-emerald-300 mb-2">Real-world applications</h4>
              <ul className="list-disc pl-5 space-y-1">
                {data.real_world_applications.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </section>
          ) : null}
          {data.common_misconceptions?.length ? (
            <section>
              <h4 className="text-xs font-semibold text-red-300/90 mb-2">Common misconceptions</h4>
              <ul className="space-y-2">
                {data.common_misconceptions.map((m, i) => (
                  <li key={i} className="text-xs border border-slate-700 rounded p-2">
                    {m.concept ? <span className="text-violet-300">{m.concept}: </span> : null}
                    <span className="text-red-200/90">{m.misconception}</span>
                    {m.correction ? (
                      <p className="text-slate-400 mt-1">→ {m.correction}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {data.visual_mind_map ? (
            <section>
              <h4 className="text-xs font-semibold text-sky-300 mb-2">Mind map outline</h4>
              <pre className="text-xs font-mono text-slate-400 whitespace-pre-wrap bg-slate-950/50 p-3 rounded border border-slate-700">
                {data.visual_mind_map}
              </pre>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}
